import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from '../seo/pages.json';

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
};

const organization = {
  '@type': 'Organization',
  '@id': `${seoConfig.origin}/#organization`,
  name: 'GKV-Consulting GmbH',
  url: 'https://gkv-consulting.com/',
  logo: `${seoConfig.origin}/new-helply-logo.png`,
  email: 'support@gkv-consulting.com'
};

const buildStructuredData = (page) => {
  const graph = [organization];
  if (page.schema === 'webapp') {
    graph.push({
      '@type': 'WebApplication',
      '@id': `${seoConfig.origin}/#webapp`,
      name: 'Helply Swiss',
      url: seoConfig.origin,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: page.description,
      provider: { '@id': `${seoConfig.origin}/#organization` }
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
};

export const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const page = seoConfig.publicPages[normalizedPath];
    const isPublic = Boolean(page);
    const title = page?.title || 'Secure Helply Account Area';
    const description = page?.description || 'Private Helply account area.';

    document.title = title;
    ensureMeta('meta[name="description"]', { name: 'description', content: description });
    ensureMeta('meta[name="robots"]', {
      name: 'robots',
      content: isPublic
        ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
        : 'noindex,nofollow,noarchive'
    });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (isPublic) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', page.canonical);
    } else if (canonical) {
      canonical.remove();
    }

    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    ensureMeta('meta[property="og:image"]', { property: 'og:image', content: `${seoConfig.origin}/new-helply-logo.png` });
    const ogUrl = document.head.querySelector('meta[property="og:url"]');
    if (isPublic) {
      ensureMeta('meta[property="og:url"]', { property: 'og:url', content: page.canonical });
    } else if (ogUrl) {
      ogUrl.remove();
    }

    let schema = document.getElementById('seo-structured-data');
    if (isPublic) {
      if (!schema) {
        schema = document.createElement('script');
        schema.id = 'seo-structured-data';
        schema.type = 'application/ld+json';
        document.head.appendChild(schema);
      }
      schema.textContent = JSON.stringify(buildStructuredData(page));
    } else if (schema) {
      schema.remove();
    }
  }, [pathname]);

  return null;
};